import * as Sentry from "@sentry/react";
import React from "react";
import ReactDOM from "react-dom/client";

import { Provider, getServerData, getTemplate } from "@reactivated";

import { activateTranslations } from "@thelabnyc/thelabui/src/utils/i18n";

import { PreferredLocationProvider } from "./hooks/preferred-location";

import "./styles/global.scss";

const { props, context } = getServerData();

if (context.SENTRY_DSN) {
    Sentry.init({
        dsn: context.SENTRY_DSN,
        release: context.CI_COMMIT_SHA,
        environment: context.CI_COMMIT_REF_SLUG,
        sampleRate: 0.01, // Capture 1% of errors
        integrations: [],
        ignoreErrors: [
            // Third-party script promise rejection noise (REDLOBSTER-CMS-2P).
            // jQuery-like AJAX error object from a GTM/third-party widget with
            // status 0 (cancelled request). Not from our code.
            "Object captured as promise rejection with keys: code, data, message, status, statusText",
            // Random plugins/extensions
            "top.GLOBALS",
            // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
            "originalCreateNotification",
            "canvas.contentDocument",
            "MyApp_RemoveAllHighlights",
            "http://tt.epicplay.com",
            "Can't find variable: ZiteReader",
            "jigsaw is not defined",
            "ComboSearch is not defined",
            "http://loading.retry.widdit.com/",
            "atomicFindClose",
            // Facebook borked
            "fb_xd_fragment",
            // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
            // reduce this. (thanks @acdha)
            // See http://stackoverflow.com/questions/4113268
            "bmi_SafeAddOnload",
            "EBCallBackMessageReceived",
            // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
            "conduitPage",
            // ShieldSquare bot-protection CAPTCHA page intercepts the real page and
            // breaks service worker registration (REDLOBSTER-CMS-1GN)
            /service worker register failed/i,
        ],
        denyUrls: [
            // Guest relations chat bot vendor (Emplifi/Astute)
            /bot\.emplifi\.io/i,
            /astutebot\.com/i,
            // Facebook flakiness
            /graph\.facebook\.com/i,
            // Facebook blocked
            /connect\.facebook\.net\/en_US\/all\.js/i,
            // Woopra flakiness
            /eatdifferent\.com\.woopra-ns\.com/i,
            /static\.woopra\.com\/js\/woopra\.js/i,
            // Chrome extensions
            /extensions\//i,
            /^chrome:\/\//i,
            /^chrome-extension:\/\//i,
            // Other plugins
            /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
            /webappstoolbarba\.texthelp\.com\//i,
            /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
            // Google maps
            /maps\.googleapis\.com\//i,
            // EUDA consent/data privacy script (REDLOBSTER-CMS-1EB, REDLOBSTER-CMS-1EC)
            /eudaapi\/get-script\//i,
            // Stormcaster third-party analytics/tracking script (REDLOBSTER-CMS-1H4)
            /stormcaster\.js/i,
            // Pinterest tracking pixel (REDLOBSTER-CMS-1H4)
            /ct\.pinterest\.com/i,
        ],
    });
}

activateTranslations(context.i18n);

const main = async () => {
    const Template = await getTemplate(context);

    ReactDOM.hydrateRoot(
        document,
        <React.StrictMode>
            <Provider value={context}>
                <PreferredLocationProvider>
                    <Template {...props} />
                </PreferredLocationProvider>
            </Provider>
        </React.StrictMode>,
    );
};

await main();
